/**
 * RepositoryContext — Dependency Injection for IFlexRepository
 *
 * All pages and components access the repository through useRepository().
 * Swapping the backend (Dexie → Supabase) only requires changing the
 * value passed to the Provider in App.tsx.
 *
 * Initialization sequence:
 *   1. Mount provider
 *   2. Seed the database (built-in exercises, default settings, muscle states)
 *   3. Render children
 *
 * The loading state prevents any page from querying before Dexie is ready.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { IFlexRepository } from '@/types';
import { repository } from './DexieRepository';

// ── Context ──────────────────────────────────────────────────

interface RepositoryContextValue {
  repo: IFlexRepository;
  isReady: boolean;
}

const RepositoryContext = createContext<RepositoryContextValue | null>(null);

// ── Provider ─────────────────────────────────────────────────

interface RepositoryProviderProps {
  children: ReactNode;
  /** Override for testing or future cloud backend */
  implementation?: IFlexRepository;
}

export function RepositoryProvider({
  children,
  implementation = repository,
}: RepositoryProviderProps) {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (implementation as typeof repository).seed?.()
      .then(() => setIsReady(true))
      .catch((err) => {
        console.error('[FlexEngine] DB seed failed:', err);
        setError(String(err));
      });
  }, [implementation]);

  if (error) {
    return (
      <div className="h-[100dvh] flex flex-col items-center justify-center px-6 text-center">
        <p className="text-danger font-semibold text-lg mb-2">Database Error</p>
        <p className="text-ink-muted text-sm font-mono bg-surface-200 rounded-xl p-4 max-w-sm break-all">
          {error}
        </p>
        <button
          className="btn-secondary mt-6"
          onClick={() => window.location.reload()}
        >
          Reload App
        </button>
      </div>
    );
  }

  if (!isReady) {
    return (
      <div className="h-[100dvh] flex flex-col items-center justify-center gap-4">
        {/* Animated logo mark */}
        <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center
                        animate-pulse-slow">
          <svg viewBox="0 0 24 24" className="w-8 h-8 text-accent" fill="none"
            stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
          </svg>
        </div>
        <p className="text-ink-muted text-sm">Initializing FlexEngine…</p>
      </div>
    );
  }

  return (
    <RepositoryContext.Provider value={{ repo: implementation, isReady }}>
      {children}
    </RepositoryContext.Provider>
  );
}

// ── Consumer Hook ─────────────────────────────────────────────

/**
 * Returns the active IFlexRepository implementation.
 * Must be called inside a RepositoryProvider.
 */
export function useRepository(): IFlexRepository {
  const ctx = useContext(RepositoryContext);
  if (!ctx) {
    throw new Error('useRepository() must be called inside <RepositoryProvider>');
  }
  return ctx.repo;
}

/** Returns true once the DB seed has completed. */
export function useDBReady(): boolean {
  const ctx = useContext(RepositoryContext);
  return ctx?.isReady ?? false;
}
